export type AndroidBuildEnvironment = 'dev' | 'prod';

export type AndroidAppIdentity = {
  environment: AndroidBuildEnvironment;
  appId: string;
  appName: string;
};

export type AndroidBuildUrls = {
  environment: AndroidBuildEnvironment;
  frontendBaseUrl: string;
  apiBaseUrl: string;
};

/** Same rules as infra/scripts/derive-env.sh: API host is api.{frontend-host}. */
export function deriveApiBaseUrl(frontendBaseUrl: string): string {
  const url = new URL(frontendBaseUrl);
  return `${url.protocol}//api.${url.hostname}`;
}

/** Baked-in VM URLs for Android builds — read FRONTEND_BASE_URL / API_BASE_URL from the environment (GitHub secrets, derive-env.sh, or build_android.local.ps1). */
export function resolveAndroidBuildUrls(environment?: string): AndroidBuildUrls {
  const env: AndroidBuildEnvironment = environment === 'prod' ? 'prod' : 'dev';
  const frontendBaseUrl = process.env.FRONTEND_BASE_URL?.trim();
  if (!frontendBaseUrl) {
    throw new Error(
      'FRONTEND_BASE_URL is required (set via GitHub secrets, derive-env.sh, or build_android.local.ps1)',
    );
  }
  const apiBaseUrl = process.env.API_BASE_URL?.trim() || deriveApiBaseUrl(frontendBaseUrl);
  return {
    environment: env,
    frontendBaseUrl,
    apiBaseUrl,
  };
}

/** Dev and prod APKs use different applicationIds so both can install side by side. */
export function resolveAndroidAppIdentity(environment?: string): AndroidAppIdentity {
  const env: AndroidBuildEnvironment = environment === 'prod' ? 'prod' : 'dev';
  if (env === 'prod') {
    return {
      environment: env,
      appId: 'com.couplespace.app',
      appName: 'Couple Space',
    };
  }
  return {
    environment: env,
    appId: 'com.couplespace.dev',
    appName: 'couplespace dev',
  };
}

/** Prod and dev install side by side, so each app has its own Google return scheme. */
export function androidAuthScheme(appId: string): 'couplespace' | 'couplespacedev' {
  return appId === 'com.couplespace.app' ? 'couplespace' : 'couplespacedev';
}

export function androidAuthCallbackUrl(appId: string): string {
  return `${androidAuthScheme(appId)}://auth`;
}
